document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const getProductSections = (root = document) => {
  const sections = [...root.querySelectorAll('[data-product-section]')];
  if (root instanceof Element && root.matches('[data-product-section]')) sections.unshift(root);
  return [...new Set(sections)];
};

const initializeProductForms = (root = document) => {
  getProductSections(root).forEach((section) => {
    if (section.dataset.productUiInitialized === 'true') return;
    section.dataset.productUiInitialized = 'true';

    const scope = section.querySelector('[data-product-form]');
    if (!scope) return;

    const form = scope.querySelector('form.product-form');
    const select = scope.querySelector('[data-variant-select]');
    const price = scope.querySelector('[data-product-price]');
    const priceLabel = scope.querySelector('[data-product-price-label]');
    const comparePrice = scope.querySelector('[data-compare-price]');
    const comparePriceWrapper = scope.querySelector('[data-compare-price-wrapper]');
    const unitPrice = scope.querySelector('[data-product-unit-price]');
    const unitPriceWrapper = scope.querySelector('[data-product-unit-price-wrapper]');
    const sku = scope.querySelector('[data-product-sku]');
    const skuWrapper = scope.querySelector('[data-product-sku-wrapper]');
    const availability = scope.querySelector('[data-product-availability]');
    const quantity = scope.querySelector('input[name="quantity"]');
    const quantityRules = scope.querySelector('[data-quantity-rules]');
    const submit = scope.querySelector('[data-add-to-cart]');
    const dynamicCheckout = scope.querySelector('[data-dynamic-checkout]');
    const stickyPrice = section.querySelector('[data-sticky-price]');
    const stickyVariant = section.querySelector('[data-sticky-variant]');
    const stickyButton = section.querySelector('[data-sticky-purchase-button]');
    const gallery = section.querySelector('.product-gallery');

    const inventoryLabels = {
      in_stock: section.dataset.inStockText,
      available: section.dataset.availableText,
      continue: section.dataset.continueText,
      sold_out: section.dataset.soldOutText
    };
    const chooseVariantText = section.dataset.chooseVariantText || 'Choose an option';
    const purchaseBlocked = section.dataset.purchaseBlocked === 'true';
    const quantityLabels = {
      minimum: section.dataset.minimumQuantityText || 'Minimum [count]',
      increment: section.dataset.quantityIncrementText || 'Increments of [count]',
      maximum: section.dataset.maximumQuantityText || 'Maximum [count]'
    };
    const quantityLabel = (template, count) => template.replace('[count]', String(count));

    const updateButton = (button, available, variantTitle) => {
      if (!button) return;
      const label = purchaseBlocked
        ? button.dataset.blockedLabel
        : (available ? button.dataset.availableLabel : button.dataset.soldOutLabel);
      const disabled = purchaseBlocked || !available;
      button.disabled = disabled;
      button.textContent = label;
      button.setAttribute('aria-disabled', String(disabled));
      button.setAttribute('aria-label', `${label} — ${button.dataset.productTitle}${variantTitle ? `, ${variantTitle}` : ''}`);
    };

    const activateMedia = (mediaId) => {
      if (!mediaId || !gallery) return;
      const mediaItems = [...gallery.querySelectorAll('[data-product-media-id]')];
      const activeMedia = mediaItems.find((item) => item.dataset.productMediaId === mediaId);
      if (!activeMedia) return;

      mediaItems.forEach((item) => item.removeAttribute('aria-current'));
      activeMedia.setAttribute('aria-current', 'true');
      if (gallery.firstElementChild !== activeMedia) gallery.prepend(activeMedia);
    };

    const updateUrl = (variantId) => {
      if (!variantId || !window.history?.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    };

    const updateQuantityRules = (option, resetQuantity) => {
      if (!quantity) return;

      const minimum = Number(option.dataset.quantityMin) || 1;
      const increment = Number(option.dataset.quantityIncrement) || 1;
      const maximum = option.dataset.quantityMax ? Number(option.dataset.quantityMax) : null;

      quantity.min = String(minimum);
      quantity.step = String(increment);
      if (maximum !== null && Number.isFinite(maximum)) quantity.max = String(maximum);
      else quantity.removeAttribute('max');

      const currentQuantity = Number(quantity.value);
      const offset = (currentQuantity - minimum) / increment;
      const isValid = Number.isFinite(currentQuantity)
        && currentQuantity >= minimum
        && (maximum === null || currentQuantity <= maximum)
        && Math.abs(offset - Math.round(offset)) < 0.000001;

      if (resetQuantity || !isValid) quantity.value = String(minimum);

      if (quantityRules) {
        const rules = [];
        if (minimum > 1) rules.push(quantityLabel(quantityLabels.minimum, minimum));
        if (increment > 1) rules.push(quantityLabel(quantityLabels.increment, increment));
        if (maximum !== null && Number.isFinite(maximum)) rules.push(quantityLabel(quantityLabels.maximum, maximum));
        quantityRules.textContent = rules.join(' · ');
        quantityRules.hidden = rules.length === 0;
      }
    };

    const updatePurchaseUi = (option, { selectionConfirmed, updateBrowserUrl = false, resetQuantity = false } = {}) => {
      if (!option) return;

      const available = option.dataset.available === 'true';
      const variantTitle = option.dataset.variantTitle || option.textContent.trim();
      const inventoryState = option.dataset.inventoryState || (available ? 'available' : 'sold_out');

      if (dynamicCheckout) dynamicCheckout.hidden = purchaseBlocked || !selectionConfirmed || !available;

      if (price && option.dataset.price) price.textContent = option.dataset.price;
      if (priceLabel) {
        const label = option.dataset.onSale === 'true' ? priceLabel.dataset.salePriceLabel : priceLabel.dataset.priceLabel;
        priceLabel.textContent = `${label}：`;
      }

      if (comparePrice) comparePrice.textContent = option.dataset.comparePrice || '';
      if (comparePriceWrapper) comparePriceWrapper.hidden = !option.dataset.comparePrice;

      if (unitPriceWrapper && unitPrice) {
        const referenceValue = option.dataset.unitReferenceValue;
        const referenceUnit = option.dataset.unitReferenceUnit;
        unitPrice.textContent = option.dataset.unitPrice
          ? `${option.dataset.unitPrice} / ${referenceValue && referenceValue !== '1' ? referenceValue : ''}${referenceUnit || ''}`
          : '';
        unitPriceWrapper.hidden = !option.dataset.unitPrice;
      }

      if (skuWrapper && sku) {
        sku.textContent = option.dataset.sku || '';
        skuWrapper.hidden = !option.dataset.sku;
      }

      if (availability) {
        availability.dataset.inventoryState = inventoryState;
        availability.textContent = inventoryLabels[inventoryState] || '';
      }

      updateQuantityRules(option, resetQuantity);
      updateButton(submit, available, variantTitle);

      if (stickyPrice && option.dataset.price) stickyPrice.textContent = option.dataset.price;
      if (stickyVariant) stickyVariant.textContent = selectionConfirmed ? variantTitle : chooseVariantText;

      if (stickyButton) {
        stickyButton.dataset.selectionConfirmed = String(selectionConfirmed);
        if (purchaseBlocked) {
          stickyButton.type = 'button';
          stickyButton.removeAttribute('name');
          stickyButton.removeAttribute('form');
          stickyButton.removeAttribute('aria-controls');
          updateButton(stickyButton, false, variantTitle);
        } else if (selectionConfirmed) {
          stickyButton.type = 'submit';
          stickyButton.name = 'add';
          stickyButton.setAttribute('form', section.dataset.productFormId);
          stickyButton.removeAttribute('aria-controls');
          updateButton(stickyButton, available, variantTitle);
        } else {
          stickyButton.type = 'button';
          stickyButton.removeAttribute('name');
          stickyButton.removeAttribute('form');
          stickyButton.setAttribute('aria-controls', select.id);
          stickyButton.disabled = false;
          stickyButton.textContent = stickyButton.dataset.chooseLabel;
          stickyButton.setAttribute('aria-disabled', 'false');
          stickyButton.setAttribute('aria-label', `${stickyButton.dataset.chooseLabel} — ${stickyButton.dataset.productTitle}`);
        }
      }

      activateMedia(option.dataset.featuredMediaId);
      if (updateBrowserUrl) updateUrl(option.value);
    };

    form?.addEventListener('submit', (event) => {
      if (!purchaseBlocked) return;
      event.preventDefault();
      scope.querySelector('.product-completeness-note')?.focus({ preventScroll: false });
    });

    if (!select) return;

    let selectionConfirmed = stickyButton?.dataset.selectionConfirmed === 'true';

    stickyButton?.addEventListener('click', (event) => {
      if (stickyButton.dataset.selectionConfirmed === 'true') return;
      event.preventDefault();
      select.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center'
      });
      select.focus({ preventScroll: true });
    });

    select.addEventListener('change', () => {
      selectionConfirmed = true;
      updatePurchaseUi(select.options[select.selectedIndex], {
        selectionConfirmed,
        updateBrowserUrl: true,
        resetQuantity: true
      });
    });

    const initialOption = select.options[select.selectedIndex];
    if (initialOption?.value) {
      updatePurchaseUi(initialOption, { selectionConfirmed });
    } else {
      if (submit) {
        submit.disabled = true;
        submit.textContent = purchaseBlocked ? submit.dataset.blockedLabel : submit.dataset.chooseLabel;
        submit.setAttribute('aria-disabled', 'true');
        submit.setAttribute(
          'aria-label',
          `${purchaseBlocked ? submit.dataset.blockedLabel : submit.dataset.chooseLabel} — ${submit.dataset.productTitle}`
        );
      }
      if (stickyVariant) stickyVariant.textContent = chooseVariantText;
    }
  });
};

const initializeProductRecommendations = (root = document) => {
  getProductSections(root).forEach((section) => {
    const container = section.querySelector('[data-product-recommendations]');
    if (!container || container.dataset.initialized === 'true' || !container.dataset.url) return;
    container.dataset.initialized = 'true';

    fetch(container.dataset.url, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Recommendation request failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const responseDocument = new DOMParser().parseFromString(html, 'text/html');
        const replacement = responseDocument.querySelector('[data-product-recommendations]');
        if (!replacement || !replacement.innerHTML.trim()) return;
        container.innerHTML = replacement.innerHTML;
        container.hidden = false;
      })
      .catch(() => {
        container.hidden = true;
      });
  });
};

const initializeCollectionControls = (root = document) => {
  root.querySelectorAll('.collection-controls').forEach((form) => {
    if (form.dataset.initialized === 'true') return;
    form.dataset.initialized = 'true';
    const groups = [...form.querySelectorAll('[data-filter-group]')];
    const sort = form.querySelector('.collection-toolbar__select');
    const sortSubmit = form.querySelector('.collection-toolbar__submit');
    const context = new URLSearchParams(window.location.search);
    const fields = (group) => [...group.querySelectorAll('input[name]')];
    const close = (group, returnFocus = false) => {
      if (!group.open) return;
      group.open = false;
      if (returnFocus) group.querySelector('summary')?.focus();
    };
    const alignPanel = (group) => {
      const panel = group.querySelector('.collection-filter__body');
      if (!panel) return;
      const bounds = form.getBoundingClientRect();
      const anchor = group.getBoundingClientRect();
      panel.style.right = 'auto';
      if (window.innerWidth <= 749) {
        panel.style.left = '0px';
      } else {
        const left = Math.max(bounds.left, Math.min(anchor.left, bounds.right - panel.offsetWidth));
        panel.style.left = `${left - anchor.left}px`;
      }
      panel.style.top = '100%';
      panel.style.bottom = 'auto';
      const below = window.innerHeight - panel.getBoundingClientRect().top - 16;
      const above = (window.innerWidth <= 749 ? bounds.top : anchor.top) - 16;
      let available = below;
      if (below < 180 && above > below) {
        panel.style.top = 'auto';
        panel.style.bottom = '100%';
        available = above;
      }
      panel.style.maxHeight = `${Math.max(0, Math.min(490, available))}px`;
    };

    ['q', 'type', 'options[prefix]'].forEach((name) => {
      const input = [...form.querySelectorAll('input[type="hidden"]')].find((field) => field.name === name);
      if (input && context.has(name)) input.value = context.get(name);
    });
    const knownNames = new Set([...form.querySelectorAll('[name]')].map((input) => input.name));
    context.forEach((value, name) => {
      if (!name.startsWith('filter.') || knownNames.has(name)) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.append(input);
    });
    form.closest('[data-catalog-controls]')?.querySelectorAll('[data-filter-context-link]').forEach((link) => {
      const url = new URL(link.href, window.location.href);
      ['q', 'type', 'options[prefix]', 'sort_by'].forEach((name) => {
        if (context.has(name)) url.searchParams.set(name, context.get(name));
      });
      link.href = url.pathname + url.search;
    });

    groups.forEach((group) => {
      group.open = false;
      const summary = group.querySelector('summary');
      const reset = group.querySelector('[data-filter-reset]');
      const resetLink = group.querySelector('[data-filter-reset-link]');
      const apply = group.querySelector('[data-filter-apply]');
      const search = group.querySelector('[data-filter-search]');
      if (reset) reset.hidden = false;
      if (resetLink) resetLink.hidden = true;
      const searchWrap = group.querySelector('[data-filter-search-wrap]');
      if (searchWrap) searchWrap.hidden = false;
      summary?.addEventListener('click', () => {
        if (!group.open) groups.forEach((other) => { if (other !== group) close(other); });
      });
      group.addEventListener('toggle', () => {
        if (!group.open) return;
        groups.forEach((other) => { if (other !== group) close(other); });
        alignPanel(group);
      });
      reset?.addEventListener('click', () => fields(group).forEach((input) => {
        if (input.type === 'checkbox') input.checked = false;
        else input.value = '';
      }));
      group.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' || event.target.tagName !== 'INPUT') return;
        event.preventDefault();
        if (event.target === search) return;
        form.requestSubmit(apply);
      });
      search?.addEventListener('input', () => {
        const query = search.value.trim().toLocaleLowerCase();
        const visibleLetters = new Set();
        group.querySelectorAll('[data-filter-value]').forEach((item) => {
          item.hidden = !item.dataset.filterValue.toLocaleLowerCase().includes(query);
          if (!item.hidden) visibleLetters.add(item.dataset.filterInitial);
        });
        group.querySelectorAll('[data-filter-letter]').forEach((letter) => {
          letter.hidden = !visibleLetters.has(letter.dataset.filterLetter);
        });
        const empty = group.querySelector('[data-filter-empty]');
        if (empty) empty.hidden = visibleLetters.size > 0;
      });
    });
    sort?.addEventListener('change', () => {
      form.requestSubmit(sortSubmit);
    });
    form.addEventListener('submit', () => {
      form.querySelectorAll('.collection-filter__price-input').forEach((input) => {
        if (input.value === '') input.disabled = true;
      });
    });
    document.addEventListener('click', (event) => {
      groups.forEach((group) => { if (!group.contains(event.target)) close(group); });
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const openGroup = groups.find((group) => group.open);
      if (!openGroup) return;
      event.preventDefault();
      close(openGroup, true);
    });
    window.addEventListener('resize', () => groups.forEach((group) => {
      if (group.open) alignPanel(group);
    }));
    window.addEventListener('pageshow', () => {
      form.querySelectorAll('.collection-filter__price-input').forEach((input) => {
        input.disabled = false;
      });
    });
    if (sortSubmit) sortSubmit.hidden = true;
  });
};

const initializeGlobalDisclosures = () => {
  if (document.documentElement.dataset.disclosuresInitialized === 'true') return;
  document.documentElement.dataset.disclosuresInitialized = 'true';
  let dismissedDropdown = null;
  const supportsHoverNavigation = () => window.matchMedia('(hover: hover) and (min-width: 1101px)').matches;

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse' || !supportsHoverNavigation() || !(event.target instanceof Element)) return;
    const disclosure = event.target.closest('.boutique-header .header-dropdown');
    if (!disclosure || disclosure.contains(event.relatedTarget)) return;
    dismissedDropdown = null;
    disclosure.open = true;
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse' || !supportsHoverNavigation() || !(event.target instanceof Element)) return;
    const disclosure = event.target.closest('.boutique-header .header-dropdown');
    if (!disclosure || disclosure.contains(event.relatedTarget)) return;
    if (!disclosure.contains(document.activeElement)) disclosure.open = false;
    if (dismissedDropdown === disclosure) dismissedDropdown = null;
  });

  document.addEventListener('focusin', (event) => {
    if (!supportsHoverNavigation() || !(event.target instanceof Element)) return;
    const disclosure = event.target.closest('.boutique-header .header-dropdown');
    if (disclosure && dismissedDropdown !== disclosure) disclosure.open = true;
  });

  document.addEventListener('focusout', (event) => {
    if (!(event.target instanceof Element)) return;
    const disclosure = event.target.closest('.boutique-header .header-dropdown');
    if (!disclosure || disclosure.contains(event.relatedTarget)) return;
    if (!disclosure.matches(':hover')) disclosure.open = false;
    if (dismissedDropdown === disclosure) dismissedDropdown = null;
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const header = event.target.closest('.site-header');
    if (!header) {
      document.querySelectorAll('.site-header details[open]').forEach((details) => details.removeAttribute('open'));
      dismissedDropdown = null;
      return;
    }

    if (event.target.closest('.header-dropdown__summary')) dismissedDropdown = null;

    if (event.target.closest('.header-dropdown__panel a')) {
      header.querySelectorAll('.header-dropdown[open]').forEach((details) => details.removeAttribute('open'));
    }
  });

  document.addEventListener('toggle', (event) => {
    const disclosure = event.target;
    if (!(disclosure instanceof HTMLDetailsElement) || !disclosure.matches('.header-dropdown') || !disclosure.open) return;

    const header = disclosure.closest('.site-header');
    header?.querySelectorAll('.header-dropdown[open]').forEach((item) => {
      if (item !== disclosure) item.removeAttribute('open');
    });
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const openDisclosures = [...document.querySelectorAll('.site-header details[open]')]
      .filter((disclosure) => disclosure.getClientRects().length > 0);
    const lastDisclosure = openDisclosures.at(-1);
    if (!lastDisclosure) return;

    dismissedDropdown = lastDisclosure;
    lastDisclosure.removeAttribute('open');
    lastDisclosure.querySelector(':scope > summary')?.focus();
  });
};

document.addEventListener('DOMContentLoaded', () => {
  initializeProductForms();
  initializeProductRecommendations();
  initializeCollectionControls();
  initializeGlobalDisclosures();
});

document.addEventListener('shopify:section:load', (event) => {
  initializeProductForms(event.target);
  initializeProductRecommendations(event.target);
  initializeCollectionControls(event.target);
});
